import React, { useState } from 'react';
import { Typography, TextField, Button, Card, Box, Container } from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import logo from '../assets/logo0.png';
import backgroundLogin from '../assets/backgroundlogin.png';

const ForgotPassword = () => {
  const { resetPassword } = useAuth();
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (!email) {
      setError('Please enter an email address');
      return;
    }

    try {
      await resetPassword(email);
      setMessage('Check your inbox for further instructions.');
      setEmail('');
    } catch (err) {
      const errorMessage = err?.message || 'Failed to reset password. Please try again.';
      setError(errorMessage);
      console.error('Password reset error:', err);
    }
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      backgroundImage: `url(${backgroundLogin})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat'
    }}>
      {/* Header */}
      <Box sx={{
        width: '100%',
        backgroundColor: '#000000',
        padding: '20px 0',
        marginBottom: '20px'
      }}>
        <Container maxWidth="lg" sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Box
            component="img"
            src={logo}
            alt="Larrivarie's Logo"
            sx={{
              height: 'auto',
              width: '150px',
              filter: 'brightness(0) invert(1)',
              objectFit: 'contain'
            }}
          />
        </Container>
      </Box>

      {/* Main Content */}
      <Container sx={{
        flex: 1,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '40px 20px'
      }}>
        <Card sx={{
          padding: '40px',
          maxWidth: '450px',
          width: '100%',
          backgroundColor: 'white',
          borderRadius: '4px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
        }}>
          <Typography 
            variant="h6" 
            sx={{ 
              marginBottom: '30px',
              color: '#333',
              fontWeight: 500
            }}
          >
            Reset Password
          </Typography>
          
          {message && (
            <Typography 
              color="success.main" 
              sx={{ marginBottom: '16px' }}
            >
              {message}
            </Typography>
          )}
          {error && (
            <Typography 
              color="error" 
              sx={{ marginBottom: '16px' }}
            >
              {error}
            </Typography>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              label="Email / Username"
              type="email"
              variant="outlined"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              sx={{ 
                marginBottom: '24px',
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'white',
                  '& fieldset': {
                    borderColor: '#e0e0e0',
                  },
                  '&:hover fieldset': {
                    borderColor: '#999',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#666',
                  }
                },
                '& .MuiInputLabel-root': {
                  color: '#666'
                }
              }}
            />
            <Button 
              type="submit" 
              variant="contained" 
              fullWidth
              sx={{
                padding: '12px',
                backgroundColor: '#008744',
                '&:hover': {
                  backgroundColor: '#006633'
                },
                textTransform: 'none',
                fontSize: '16px',
                marginBottom: '20px'
              }}
            >
              Reset Password
            </Button>
          </form>

          <Box sx={{ textAlign: 'center' }}>
            <Typography 
              variant="body2" 
              sx={{ 
                color: '#666',
                marginBottom: '16px'
              }}
            >
              Not a customer yet?
            </Typography>
            <Button
              variant="outlined"
              fullWidth
              sx={{
                padding: '10px',
                color: '#008744',
                borderColor: '#008744',
                '&:hover': {
                  borderColor: '#006633',
                  backgroundColor: 'rgba(0,135,68,0.04)'
                },
                textTransform: 'none'
              }}
              onClick={() => navigate('/signup')}
            >
              Create Customer Account
            </Button>
          </Box>
        </Card>
      </Container>

      {/* New Footer */}
      <Box sx={{
        backgroundColor: '#2d2d2d',
        color: '#fff',
        padding: '40px 0',
        marginTop: 'auto'
      }}>
        <Container maxWidth="lg">
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' },
            gap: 4,
            mb: 4
          }}>
            {/* Column 1 */}
            <Box>
              <Box
                component="img"
                src={logo}
                alt="Larrivarie's Logo"
                sx={{
                  height: 'auto',
                  width: '100px',
                  filter: 'brightness(0) invert(1)',
                  mb: 2
                }}
              />
              <Typography variant="body2" sx={{ color: '#9e9e9e' }}>
                Your trusted partner in business solutions
              </Typography>
            </Box>

            {/* Column 2 */}
            <Box>
              <Typography variant="h6" sx={{ mb: 2, color: '#fff' }}>
                Products
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {['Teams', 'Advertising', 'Talent', 'Enterprise'].map((item) => (
                  <Button
                    key={item}
                    sx={{
                      color: '#9e9e9e',
                      textAlign: 'left',
                      textTransform: 'none',
                      justifyContent: 'flex-start',
                      '&:hover': { color: '#fff' }
                    }}
                  >
                    {item}
                  </Button>
                ))}
              </Box>
            </Box>

            {/* Column 3 */}
            <Box>
              <Typography variant="h6" sx={{ mb: 2, color: '#fff' }}>
                Company
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {['About', 'Press', 'Work Here', 'Legal'].map((item) => (
                  <Button
                    key={item}
                    sx={{
                      color: '#9e9e9e',
                      textAlign: 'left',
                      textTransform: 'none',
                      justifyContent: 'flex-start',
                      '&:hover': { color: '#fff' }
                    }}
                  >
                    {item}
                  </Button>
                ))}
              </Box>
            </Box>

            {/* Column 4 */}
            <Box>
              <Typography variant="h6" sx={{ mb: 2, color: '#fff' }}>
                Connect
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {['Blog', 'Twitter', 'LinkedIn', 'Contact Us'].map((item) => (
                  <Button
                    key={item}
                    sx={{
                      color: '#9e9e9e',
                      textAlign: 'left',
                      textTransform: 'none',
                      justifyContent: 'flex-start',
                      '&:hover': { color: '#fff' }
                    }}
                  >
                    {item}
                  </Button>
                ))}
              </Box>
            </Box>
          </Box>

          <Box sx={{
            borderTop: '1px solid #4d4d4d',
            paddingTop: 3,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2
          }}>
            <Typography variant="body2" sx={{ color: '#9e9e9e' }}>
              © 2024 Larrivarie. All rights reserved.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button sx={{ color: '#9e9e9e', textTransform: 'none', '&:hover': { color: '#fff' } }}>
                Privacy Policy
              </Button>
              <Button sx={{ color: '#9e9e9e', textTransform: 'none', '&:hover': { color: '#fff' } }}>
                Terms of Service
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default ForgotPassword; 